import React, { useState, useEffect, useRef } from 'react';
import { Edit2, X, Loader, Building2, ChevronDown, Check, Search, Camera } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE_URL } from '../../config/api';
import { format } from 'date-fns';
import { ImageUploadDropZone } from '../common/ImageUploadDropZone';

interface PracticeData {
  uuid: string;
  name: string;
  phone?: string;
  email?: string;
  // Address fields (USPS standard)
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  address?: string; // Computed full address for display
  license_number?: string;
  description?: string;
  website?: string;
  timezone?: string;
  accepts_new_patients: boolean;
  is_active: boolean;
  email_notifications_enabled: boolean;
  // Online booking URL fields
  slug?: string;
  hours?: Array<{
    day: string;
    open: string;
    close: string;
    closed: boolean;
  }>;
  // Practice photo
  photo_url?: string;
}

const formatBusinessHoursTime = (hhmm?: string) => {
  if (!hhmm) return '';
  const [hRaw, mRaw] = hhmm.split(':');
  const h = Number(hRaw);
  const m = Number(mRaw);
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return format(d, 'h:mm a');
};

const PracticeSettings: React.FC = () => {
  const { user } = useAuth();
  const [practice, setPractice] = useState<PracticeData | null>(null);
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    // Address fields (USPS standard)
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    license_number: '',
    description: '',
    website: '',
    accepts_new_patients: true,
    email_notifications_enabled: true,
    timezone: 'America/Los_Angeles',
    // Online booking URL fields
    slug: '',
    hours: [
      { day: 'Monday', open: '09:00', close: '17:00', closed: false },
      { day: 'Tuesday', open: '09:00', close: '17:00', closed: false },
      { day: 'Wednesday', open: '09:00', close: '17:00', closed: false },
      { day: 'Thursday', open: '09:00', close: '17:00', closed: false },
      { day: 'Friday', open: '09:00', close: '17:00', closed: false },
      { day: 'Saturday', open: '09:00', close: '17:00', closed: false },
      { day: 'Sunday', open: '09:00', close: '17:00', closed: true },
    ],
  });

  // US Timezones
  const US_TIMEZONES = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Phoenix', label: 'Mountain Time - Arizona (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  ];

  // US States
  const US_STATES = [
    { value: 'al', label: 'Alabama' },
    { value: 'ak', label: 'Alaska' },
    { value: 'az', label: 'Arizona' },
    { value: 'ar', label: 'Arkansas' },
    { value: 'ca', label: 'California' },
    { value: 'co', label: 'Colorado' },
    { value: 'ct', label: 'Connecticut' },
    { value: 'de', label: 'Delaware' },
    { value: 'fl', label: 'Florida' },
    { value: 'ga', label: 'Georgia' },
    { value: 'hi', label: 'Hawaii' },
    { value: 'id', label: 'Idaho' },
    { value: 'il', label: 'Illinois' },
    { value: 'in', label: 'Indiana' },
    { value: 'ia', label: 'Iowa' },
    { value: 'ks', label: 'Kansas' },
    { value: 'ky', label: 'Kentucky' },
    { value: 'la', label: 'Louisiana' },
    { value: 'me', label: 'Maine' },
    { value: 'md', label: 'Maryland' },
    { value: 'ma', label: 'Massachusetts' },
    { value: 'mi', label: 'Michigan' },
    { value: 'mn', label: 'Minnesota' },
    { value: 'ms', label: 'Mississippi' },
    { value: 'mo', label: 'Missouri' },
    { value: 'mt', label: 'Montana' },
    { value: 'ne', label: 'Nebraska' },
    { value: 'nv', label: 'Nevada' },
    { value: 'nh', label: 'New Hampshire' },
    { value: 'nj', label: 'New Jersey' },
    { value: 'nm', label: 'New Mexico' },
    { value: 'ny', label: 'New York' },
    { value: 'nc', label: 'North Carolina' },
    { value: 'nd', label: 'North Dakota' },
    { value: 'oh', label: 'Ohio' },
    { value: 'ok', label: 'Oklahoma' },
    { value: 'or', label: 'Oregon' },
    { value: 'pa', label: 'Pennsylvania' },
    { value: 'ri', label: 'Rhode Island' },
    { value: 'sc', label: 'South Carolina' },
    { value: 'sd', label: 'South Dakota' },
    { value: 'tn', label: 'Tennessee' },
    { value: 'tx', label: 'Texas' },
    { value: 'ut', label: 'Utah' },
    { value: 'vt', label: 'Vermont' },
    { value: 'va', label: 'Virginia' },
    { value: 'wa', label: 'Washington' },
    { value: 'wv', label: 'West Virginia' },
    { value: 'wi', label: 'Wisconsin' },
    { value: 'wy', label: 'Wyoming' },
    { value: 'dc', label: 'Washington D.C.' },
  ];

  // Auto-generate slug from practice name
  const generateSlug = (name: string) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  };

  // State dropdown search
  const [stateSearch, setStateSearch] = useState('');
  const [stateDropdownOpen, setStateDropdownOpen] = useState(false);
  const stateDropdownRef = useRef<HTMLDivElement>(null);

  // Filter states based on search
  const filteredStates = US_STATES.filter(s => 
    s.label.toLowerCase().includes(stateSearch.toLowerCase()) ||
    s.value.toLowerCase().includes(stateSearch.toLowerCase())
  );

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (stateDropdownRef.current && !stateDropdownRef.current.contains(event.target as Node)) {
        setStateDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check if user has permission to edit practice
  const canEditPractice = user?.role === 'PRACTICE_ADMIN' || user?.role === 'ADMIN';

  useEffect(() => {
    fetchPracticeDetails();
  }, [user?.practice_id]);

  const fetchPracticeDetails = async () => {
    if (!user?.practice_id) {
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/api/v1/practices/${user.practice_id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPractice(data);
        setFormData({
          name: data.name || '',
          phone: data.phone || '',
          email: data.email || '',
          // Address fields (USPS standard)
          address_line1: data.address_line1 || '',
          address_line2: data.address_line2 || '',
          city: data.city || '',
          state: data.state || '',
          zip_code: data.zip_code || '',
          license_number: data.license_number || '',
          description: data.description || '',
          website: data.website || '',
          accepts_new_patients: data.accepts_new_patients ?? true,
          email_notifications_enabled: data.email_notifications_enabled ?? true,
          timezone: data.timezone || 'America/Los_Angeles',
          slug: data.slug || '',
          hours: data.hours || [
            { day: 'Monday', open: '09:00', close: '17:00', closed: false },
            { day: 'Tuesday', open: '09:00', close: '17:00', closed: false },
            { day: 'Wednesday', open: '09:00', close: '17:00', closed: false },
            { day: 'Thursday', open: '09:00', close: '17:00', closed: false },
            { day: 'Friday', open: '09:00', close: '17:00', closed: false },
            { day: 'Saturday', open: '09:00', close: '17:00', closed: false },
            { day: 'Sunday', open: '09:00', close: '17:00', closed: true },
          ],
        });
      } else if (response.status === 404) {
        setError('No practice associated with your account');
      } else {
        throw new Error('Failed to fetch practice details');
      }
    } catch (err) {
      console.error('Error fetching practice details:', err);
      setError('Failed to load practice details');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!practice) return;

    setSaving(true);
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/api/v1/practices/${practice.uuid}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name || null,
          phone: formData.phone || null,
          email: formData.email || null,
          // Address fields (USPS standard)
          address_line1: formData.address_line1 || null,
          address_line2: formData.address_line2 || null,
          city: formData.city || null,
          state: formData.state || null,
          zip_code: formData.zip_code || null,
          license_number: formData.license_number || null,
          description: formData.description || null,
          website: formData.website || null,
          accepts_new_patients: formData.accepts_new_patients,
          email_notifications_enabled: formData.email_notifications_enabled,
          timezone: formData.timezone,
          hours: formData.hours,
          // Online booking URL - auto-generate slug from name
          slug: generateSlug(formData.name),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update practice');
      }

      const updatedPractice = await response.json();
      setPractice(updatedPractice);
      setShowEditPanel(false);
    } catch (err) {
      console.error('Error updating practice:', err);
      setError(err instanceof Error ? err.message : 'Failed to update practice');
    } finally {
      setSaving(false);
    }
  };

  // Handle practice photo upload
  const handlePhotoUpload = async (file: File) => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');

    // 1. Initiate upload - get signed S3 URL
    const initResponse = await fetch(`${API_BASE_URL}/api/v1/upload/practice/initiate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        filename: file.name,
        content_type: file.type,
        size_bytes: file.size
      })
    });

    if (!initResponse.ok) {
      const errorData = await initResponse.json();
      throw new Error(errorData.detail || 'Failed to initiate upload');
    }

    const { upload_url, fields, s3_key, file_id } = await initResponse.json();

    // 2. Upload to S3 using signed URL
    const formData = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
      formData.append(key, value as string);
    });
    formData.append('file', file);

    const uploadResponse = await fetch(upload_url, {
      method: 'POST',
      body: formData
    });

    if (!uploadResponse.ok) {
      throw new Error('Failed to upload to S3');
    }

    // 3. Complete upload
    const completeResponse = await fetch(`${API_BASE_URL}/api/v1/upload/practice/complete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        s3_key,
        filename: file.name,
        content_type: file.type,
        size_bytes: file.size,
        file_id
      })
    });

    if (!completeResponse.ok) {
      throw new Error('Failed to complete upload');
    }

    const result = await completeResponse.json();
    
    // Update local state with new photo URL
    if (practice) {
      setPractice({ ...practice, photo_url: result.photo_url });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="w-6 h-6 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (!user?.practice_id || !practice) {
    return (
      <div className="p-8">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <Building2 className="w-6 h-6 text-yellow-600 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-yellow-900">No Practice Associated</h3>
              <p className="text-sm text-yellow-700 mt-1">
                You are not currently associated with a veterinary practice.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!canEditPractice) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <Building2 className="w-6 h-6 text-red-600 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-red-900">Insufficient Permissions</h3>
              <p className="text-sm text-red-700 mt-1">
                Only practice administrators can edit practice details.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-8 py-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Practice Settings</h1>
            <p className="text-sm text-gray-600 mt-1">
              Manage your veterinary practice information
            </p>
          </div>
          <button
            onClick={() => setShowEditPanel(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
          >
            <Edit2 className="w-4 h-4" />
            Edit Practice
          </button>
        </div>
      </div>

      {/* Practice Details */}
      <div className="p-8">
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {/* Practice Header */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center gap-4">
              {/* Practice Photo Upload */}
              <div className="relative group">
                <ImageUploadDropZone
                  onUpload={handlePhotoUpload}
                  currentImageUrl={practice.photo_url}
                  variant="circle"
                  size="lg"
                />
                {!practice.photo_url && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-full h-full rounded-full bg-blue-100 flex items-center justify-center">
                      <Building2 className="w-8 h-8 text-blue-600" />
                    </div>
                  </div>
                )}
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{practice.name}</h2>
                <p className="text-sm text-gray-600">
                  {practice.is_active ? (
                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                      Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
                      Inactive
                    </span>
                  )}
                </p>
                <p className="text-xs text-gray-500 mt-1">Click to upload practice logo</p>
              </div>
            </div>
          </div>

          {/* Details Table */}
          <table className="min-w-full">
            <tbody className="divide-y divide-gray-200">
              <tr>
                <td className="px-6 py-4 text-sm font-medium text-gray-500 bg-gray-50 w-1/3">
                  Practice Name
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {practice.name}
                </td>
              </tr>
              <tr>
                <td className="px-6 py-4 text-sm font-medium text-gray-500 bg-gray-50">
                  Phone
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {practice.phone || <span className="text-gray-400">Not set</span>}
                </td>
              </tr>
              <tr>
                <td className="px-6 py-4 text-sm font-medium text-gray-500 bg-gray-50">
                  Email
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {practice.email || <span className="text-gray-400">Not set</span>}
                </td>
              </tr>
              <tr>
                <td className="px-6 py-4 text-sm font-medium text-gray-500 bg-gray-50">
                  Address
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {practice.address || <span className="text-gray-400">Not set</span>}
                </td>
              </tr>
              <tr>
                <td className="px-6 py-4 text-sm font-medium text-gray-500 bg-gray-50">
                  License Number
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {practice.license_number || <span className="text-gray-400">Not set</span>}
                </td>
              </tr>
              <tr>
                <td className="px-6 py-4 text-sm font-medium text-gray-500 bg-gray-50">
                  Website
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {practice.website ? (
                    <a href={practice.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      {practice.website}
                    </a>
                  ) : (
                    <span className="text-gray-400">Not set</span>
                  )}
                </td>
              </tr>
              <tr>
                <td className="px-6 py-4 text-sm font-medium text-gray-500 bg-gray-50">
                  Online Booking URL
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {practice.state && practice.city ? (
                    <div>
                      <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                        /book/{practice.state}/{practice.city}/{practice.slug || generateSlug(practice.name)}
                      </span>
                    </div>
                  ) : (
                    <span className="text-gray-400">Not configured - set State and City above</span>
                  )}
                </td>
              </tr>
              <tr>
                <td className="px-6 py-4 text-sm font-medium text-gray-500 bg-gray-50">
                  Description
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {practice.description || <span className="text-gray-400">Not set</span>}
                </td>
              </tr>
              <tr>
                <td className="px-6 py-4 text-sm font-medium text-gray-500 bg-gray-50">
                  Timezone
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {practice.timezone ? (
                    US_TIMEZONES.find(tz => tz.value === practice.timezone)?.label || practice.timezone
                  ) : (
                    <span className="text-gray-400">Not set</span>
                  )}
                </td>
              </tr>
              <tr>
                <td className="px-6 py-4 text-sm font-medium text-gray-500 bg-gray-50">
                  Business Hours
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {practice.hours && practice.hours.length > 0 ? (
                    <div className="space-y-1">
                      {practice.hours.map((h) => (
                        <div key={h.day} className="flex items-center gap-3">
                          <div className="w-24 text-gray-600">{h.day}</div>
                          {h.closed ? (
                            <div className="text-gray-500">Closed</div>
                          ) : (
                            <div className="text-gray-900">
                              {formatBusinessHoursTime(h.open)} – {formatBusinessHoursTime(h.close)}
                            </div>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => setShowEditPanel(true)}
                        className="mt-2 text-sm text-blue-600 hover:underline"
                      >
                        Edit business hours
                      </button>
                    </div>
                  ) : (
                    <div>
                      <span className="text-gray-400">Not set</span>
                      <div>
                        <button
                          type="button"
                          onClick={() => setShowEditPanel(true)}
                          className="mt-2 text-sm text-blue-600 hover:underline"
                        >
                          Add business hours
                        </button>
                      </div>
                    </div>
                  )}
                </td>
              </tr>
              <tr>
                <td className="px-6 py-4 text-sm font-medium text-gray-500 bg-gray-50">
                  Accepting New Patients
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {practice.accepts_new_patients ? (
                    <span className="inline-flex items-center px-3 py-2 text-sm font-semibold bg-green-100 text-green-800 border-2 border-green-400 rounded-lg uppercase">
                      Yes
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-3 py-2 text-sm font-semibold bg-gray-100 text-gray-800 border-2 border-gray-400 rounded-lg uppercase">
                      No
                    </span>
                  )}
                </td>
              </tr>
              <tr>
                <td className="px-6 py-4 text-sm font-medium text-gray-500 bg-gray-50">
                  Email Notifications
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {practice.email_notifications_enabled ? (
                    <span className="inline-flex items-center px-3 py-2 text-sm font-semibold bg-green-100 text-green-800 border-2 border-green-400 rounded-lg uppercase">
                      Enabled
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-3 py-2 text-sm font-semibold bg-red-100 text-red-800 border-2 border-red-400 rounded-lg uppercase">
                      Disabled
                    </span>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Panel - Slides in from right */}
      {showEditPanel && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/20" onClick={() => setShowEditPanel(false)} />
          <div 
            className="absolute right-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl animate-slide-in-right flex flex-col"
            style={{
              transform: 'translateX(0)',
              transition: 'transform 0.3s ease-out'
            }}
          >
            {/* Panel Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white flex-shrink-0">
              <h2 className="text-lg font-semibold text-gray-900">Edit Practice Details</h2>
              <button
                onClick={() => setShowEditPanel(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Panel Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Practice Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Address Section */}
                <hr className="border-gray-200 my-4" />
                <h4 className="text-sm font-medium text-gray-900 mb-3">Address</h4>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Street Address
                  </label>
                  <input
                    type="text"
                    value={formData.address_line1}
                    onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="123 Main St"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Street Address 2
                  </label>
                  <input
                    type="text"
                    value={formData.address_line2}
                    onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Suite 100 (optional)"
                  />
                </div>

                <div className="grid grid-cols-6 gap-3">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      City
                    </label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="San Francisco"
                    />
                  </div>
                  
                  <div ref={stateDropdownRef} className="col-span-2 relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      State
                    </label>
                    <button
                      type="button"
                      onClick={() => setStateDropdownOpen(!stateDropdownOpen)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent flex items-center justify-between"
                    >
                      <span className={formData.state ? 'text-gray-900' : 'text-gray-400'}>
                        {formData.state ? US_STATES.find(s => s.value === formData.state)?.label : 'Select...'}
                      </span>
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${stateDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {stateDropdownOpen && (
                      <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg">
                        <div className="p-2 border-b border-gray-100">
                          <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="text"
                              value={stateSearch}
                              onChange={(e) => setStateSearch(e.target.value)}
                              placeholder="Search..."
                              className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                              autoFocus
                            />
                          </div>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {filteredStates.length === 0 ? (
                            <div className="px-3 py-2 text-sm text-gray-500">No states found</div>
                          ) : (
                            filteredStates.map((s) => (
                              <button
                                key={s.value}
                                type="button"
                                onClick={() => {
                                  setFormData({ ...formData, state: s.value });
                                  setStateDropdownOpen(false);
                                  setStateSearch('');
                                }}
                                className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${
                                  formData.state === s.value ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                                }`}
                              >
                                <span>{s.label}</span>
                                {formData.state === s.value && <Check className="w-4 h-4 text-blue-600" />}
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ZIP
                    </label>
                    <input
                      type="text"
                      value={formData.zip_code}
                      onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="94102"
                      maxLength={10}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    License Number
                  </label>
                  <input
                    type="text"
                    value={formData.license_number}
                    onChange={(e) => setFormData({ ...formData, license_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Website
                  </label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="https://"
                  />
                </div>

                {/* Online Booking URL Preview */}
                {formData.state && formData.city && formData.name && (
                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Online Booking URL</h4>
                    <div className="p-2 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-600">
                      Your booking URL: <span className="font-mono text-gray-900">/book/{formData.state}/{formData.city.toLowerCase().replace(/\s+/g, '-')}/{generateSlug(formData.name)}</span>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Timezone *
                  </label>
                  <select
                    value={formData.timezone}
                    onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    {US_TIMEZONES.map((tz) => (
                      <option key={tz.value} value={tz.value}>
                        {tz.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="accepts_new_patients"
                    checked={formData.accepts_new_patients}
                    onChange={(e) => setFormData({ ...formData, accepts_new_patients: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="accepts_new_patients" className="ml-2 block text-sm text-gray-700">
                    Accepting new patients
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="email_notifications_enabled"
                    checked={formData.email_notifications_enabled}
                    onChange={(e) => setFormData({ ...formData, email_notifications_enabled: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="email_notifications_enabled" className="ml-2 block text-sm text-gray-700">
                    Enable email notifications for appointments
                  </label>
                </div>

                {/* Hours Editor */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Business Hours
                  </label>
                  <div className="space-y-3 border border-gray-300 rounded-md p-4 bg-gray-50">
                    {formData.hours.map((dayHours, index) => (
                      <div key={dayHours.day} className="flex items-center gap-3">
                        <div className="w-24 text-sm font-medium text-gray-700">{dayHours.day}</div>
                        <input
                          type="checkbox"
                          checked={!dayHours.closed}
                          onChange={(e) => {
                            const newHours = [...formData.hours];
                            newHours[index].closed = !e.target.checked;
                            setFormData({ ...formData, hours: newHours });
                          }}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        {!dayHours.closed ? (
                          <>
                            <input
                              type="time"
                              value={dayHours.open}
                              onChange={(e) => {
                                const newHours = [...formData.hours];
                                newHours[index].open = e.target.value;
                                setFormData({ ...formData, hours: newHours });
                              }}
                              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <span className="text-gray-500">to</span>
                            <input
                              type="time"
                              value={dayHours.close}
                              onChange={(e) => {
                                const newHours = [...formData.hours];
                                newHours[index].close = e.target.value;
                                setFormData({ ...formData, hours: newHours });
                              }}
                              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </>
                        ) : (
                          <span className="text-sm text-gray-500">Closed</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex gap-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowEditPanel(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {saving ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default PracticeSettings;

